/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  CreditCard, 
  QrCode, 
  Smartphone, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { PublicCheckout } from '../../api/publicEntities';


// Script loader
function useExternalScript(url) {
  const [status, setStatus] = useState(url ? 'loading' : 'idle');

  useEffect(() => {
    if (!url) {
      setStatus('idle');
      return;
    }

    // Check if the script tag already exists to avoid adding it multiple times
    let script = document.querySelector(`script[src="${url}"]`);

    if (!script) {
      script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      document.body.appendChild(script);

      const setAttributeFromEvent = (event) => {
        script.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error');
      };

      script.addEventListener('load', setAttributeFromEvent);
      script.addEventListener('error', setAttributeFromEvent);
    } else {
      // If script exists, check its status
      setStatus(script.getAttribute('data-status') || 'loading');
    }

    // A callback to set the status of our hook
    const setStateFromEvent = (event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };

    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);

    // Cleanup listeners on component unmount
    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent);
        script.removeEventListener('error', setStateFromEvent);
      }
    };
  }, [url]);

  return status;
};


const TBankPayment = ({ 
  orderId, 
  onPaymentSuccess, 
  onPaymentError,
  isVisible = false 
}) => {
  const scriptStatus = useExternalScript('https://integrationjs.tbank.ru/integration.js');
  const { t } = useTranslation();
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('idle'); // idle, loading, pending, success, error
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentId, setPaymentId] = useState(null);
  const [integration, setIntegration] = useState(null);
  const statusCheckInterval = useRef(null);
  const widgetRef = useRef(null);
  const integrationRef = useRef(null);

  // Initialize payment when component becomes visible and script is loaded
  useEffect(() => {
    if (scriptStatus === 'error') {
      setError('Failed to load payment integration');
      setPaymentStatus('error');
      return;
    }

    if (isVisible && scriptStatus === 'ready' && !paymentData && orderId) {
      initiatePayment();
    }
  }, [isVisible, scriptStatus, orderId]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (statusCheckInterval.current) {
        clearInterval(statusCheckInterval.current);
      }
      if (integrationRef.current) {
        integrationRef.current = null;
      }
    };
  }, []);

  const initiatePayment = async () => {
    try {
      setPaymentStatus('loading');
      setError(null);

      const data = await PublicCheckout.initiateTBankPayment(orderId);
      if (!data.paymentUrl || !data.paymentId) {
        throw new Error('Payment initiation failed');
      }

      setPaymentData(data);
      setPaymentId(data.paymentId);
      setPaymentUrl(data.paymentUrl);
      setPaymentStatus('pending');

      // Start status checking
      startStatusCheck(orderId);

      // Initialize T-Bank integration
      await initializeTBankIntegration(data);

    } catch (err) {
      console.error('T-Bank payment initiation error:', err);
      setError(err.message || 'Payment initiation failed');
      setPaymentStatus('error');
      onPaymentError?.(err.message || 'Payment initiation failed');
    }
  };

  const initializeTBankIntegration = async (data) => {
    try {
      if (!window.PaymentIntegration) {
        throw new Error('T-Bank integration script not loaded');
      }

      // Initialize T-Bank integration
      const initConfig = {
        terminalKey: data.terminalKey || 'default', // This should come from backend config
        product: 'eacq',
        features: {
          payment: data.features?.quick_payment ? {} : undefined,
          iframe: data.iframeEnabled ? {} : undefined
        }
      };

      const integration = await window.PaymentIntegration.init(initConfig);
      setIntegration(integration);

      // Create payment integration based on available features
      if (data.features?.quick_payment) {
        await createPaymentWidget(integration, data);
      } else if (data.iframeEnabled) {
        await createIframePayment(integration, data);
      } else {
        // Fallback to simple iframe
        loadSimpleIframe(data);
      }

    } catch (err) {
      console.error('T-Bank integration initialization error:', err);
      // Fallback to simple iframe
      loadSimpleIframe(data);
    }
  };

  const createPaymentWidget = async (integration, data) => {
    try {
      const MAIN_INTEGRATION_NAME = 'tbank-payment-widget';
      const MAIN_INTEGRATION_CONFIG = {};

      const paymentIntegration = await integration.payments.create(
        MAIN_INTEGRATION_NAME,
        MAIN_INTEGRATION_CONFIG
      );

      integrationRef.current = paymentIntegration;

      // Mount the widget
      if (widgetRef.current) {
        await paymentIntegration.mount(widgetRef.current);
      }

      // Set payment start callback
      await integration.payments.setPaymentStartCallback(async () => {
        // Return the payment URL from our backend
        return data.paymentUrl;
      });

      // Update widget types based on available features
      const widgetTypes = [];
      if (data.features?.mirpay) widgetTypes.push('mirpay');
      if (data.features?.sbp) widgetTypes.push('sbp');
      if (data.features?.tpay) widgetTypes.push('tpay');

      if (widgetTypes.length > 0) {
        await paymentIntegration.updateWidgetTypes(widgetTypes);
      }

    } catch (err) {
      console.error('Payment widget creation error:', err);
      // Fallback to simple iframe
      loadSimpleIframe(data);
    }
  };

  const createIframePayment = async (integration, data) => {
    try {
      const MAIN_INTEGRATION_NAME = 'tbank-iframe-payment';
      const iframeConfig = {};

      const iframeIntegration = await integration.iframe.create(
        MAIN_INTEGRATION_NAME,
        iframeConfig
      );

      integrationRef.current = iframeIntegration;

      // Mount the iframe
      if (widgetRef.current) {
        await iframeIntegration.mount(widgetRef.current, data.paymentUrl);
      }

    } catch (err) {
      console.error('Iframe payment creation error:', err);
      // Fallback to simple iframe
      loadSimpleIframe(data);
    }
  };

  const loadSimpleIframe = (data) => {
    // Fallback: Create simple iframe
    if (widgetRef.current) {
      widgetRef.current.innerHTML = '';
      
      const iframe = document.createElement('iframe');
      iframe.src = data.paymentUrl;
      iframe.width = '100%';
      iframe.height = '500';
      iframe.frameBorder = '0';
      iframe.style.border = 'none';
      iframe.style.borderRadius = '8px';
      
      widgetRef.current.appendChild(iframe);
    }
  };

  const startStatusCheck = (orderId) => {
    // Check status every 3 seconds
    statusCheckInterval.current = setInterval(async () => {
      try {
        const data = await PublicCheckout.getTBankPaymentStatus(orderId);
          
        if (data.status === 'completed') {
          setPaymentStatus('success');
          clearInterval(statusCheckInterval.current);
          onPaymentSuccess?.(data);
        } else if (data.status === 'failed' || data.status === 'cancelled') {
          setPaymentStatus('error');
          clearInterval(statusCheckInterval.current);
          onPaymentError?.(data.message || 'Payment was unsuccessful');
        }
      } catch (err) {
        console.error('Status check error:', err);
      }
    }, 3000);
  };

  const handleOpenInApp = () => {
    if (paymentUrl) {
      window.open(paymentUrl, '_blank');
    }
  };

  const formatAmount = (amount) => {
    return `₽${Number(amount || 0).toLocaleString()}`;
  };

  const getStatusIcon = () => {
    switch (paymentStatus) {
      case 'loading':
        return <Loader2 className="w-5 h-5 animate-spin text-blue-600" />;
      case 'pending':
        return <CreditCard className="w-5 h-5 text-amber-600" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <CreditCard className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusText = () => {
    switch (paymentStatus) {
      case 'loading':
        return t('public_order.payment.tbank.loading');
      case 'pending':
        return t('public_order.payment.tbank.pending');
      case 'success':
        return t('public_order.payment.tbank.success');
      case 'error':
        return t('public_order.payment.tbank.failed');
      default:
        return t('public_order.payment.tbank.title');
    }
  };

  const getStatusBadge = () => {
    switch (paymentStatus) {
      case 'pending':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800">Ожидание</Badge>;
      case 'success':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">Успешно</Badge>;
      case 'error':
        return <Badge variant="secondary" className="bg-red-100 text-red-800">Ошибка</Badge>;
      default:
        return null;
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {getStatusIcon()}
          {getStatusText()}
          {getStatusBadge()}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {paymentStatus === 'loading' && (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-slate-600">{t('public_order.payment.tbank.initiating')}</p>
          </div>
        )}

        {paymentStatus === 'pending' && paymentData && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-lg font-semibold text-slate-900">
                {formatAmount(paymentData.amount || 0)}
              </p>
              <p className="text-sm text-slate-600">
                {t('public_order.payment.tbank.description')}
              </p>
            </div>

            {/* T-Bank Integration Widget */}
            <div ref={widgetRef} className="w-full">
              {/* Widget will be loaded here */}
            </div>

            {/* Mobile App Button */}
            <div className="text-center">
              <Button
                onClick={handleOpenInApp}
                variant="outline"
                className="w-full"
              >
                <Smartphone className="w-4 h-4 mr-2" />
                {t('public_order.payment.tbank.mobile_app')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Payment Info */}
            <div className="bg-slate-50 p-4 rounded-lg">
              <div className="text-sm text-slate-600 space-y-1">
                <div><strong>ID платежа:</strong> {paymentId}</div>
                <div><strong>Email:</strong> {paymentData?.customerEmail || 'N/A'}</div>
                {paymentData?.customerPhone && <div><strong>Телефон:</strong> {paymentData.customerPhone}</div>}
                {paymentData?.status === 'pending' && (
                  <div className="text-xs text-amber-600 mt-2">
                    <strong>Статус:</strong> Платеж ожидает оплаты
                  </div>
                )}
                {paymentData?.isExisting && (
                  <div className="text-xs text-blue-600 mt-2">
                    <strong>Информация:</strong> Продолжение существующего платежа
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {paymentStatus === 'success' && (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h3 className="text-lg font-semibold text-green-900 mb-2">
              {t('public_order.payment.tbank.success')}
            </h3>
            <p className="text-slate-600">
              {t('public_order.payment.tbank.success_description')}
            </p>
          </div>
        )}

        {paymentStatus === 'error' && (
          <div className="text-center py-8">
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              {t('public_order.payment.tbank.failed')}
            </h3>
            <p className="text-slate-600">
              {t('public_order.payment.tbank.failed_description')}
            </p>
            <Button
              onClick={initiatePayment}
              className="mt-4"
              variant="outline"
            >
              {t('public_order.payment.tbank.retry')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TBankPayment;
